import { useState } from 'react';
import { computeTotals, formatMoney, pickForLanguage } from '@domain';
import type { LineAmountType, ReferenceData } from '@domain';
import type { MoneyEntry, MoneyLineEntry, TrainingDelivery } from '../api.js';
import { accountTypeLabel, bankAccounts, categoryAccounts, groupAccounts } from '../data/accounts.js';
import type { Lang, Translate } from '../i18n/index.js';
import { SearchableSelect, type SelectOption } from '../components/SearchableSelect.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';

/**
 * Spend money and receive money — Xero's own two, and the same form twice.
 *
 * They differ in three labels and one word in the payload, so they are one
 * component. Written separately, every rounding rule and every label would
 * have to be fixed twice, and the second one is always the one that is missed.
 *
 * What this is for: the part of a bank statement no invoice explains. A bank
 * charge, a fuel receipt, the interest a deposit earned, a cash sale. Until
 * now a client had to send those to us, which is the thing the portal exists
 * to stop.
 */

const BLANK: MoneyLineEntry = { description: '', accountCode: '', taxType: '', amount: '' };

export function SpendReceiveForm({
  direction,
  reference,
  deliveries,
  currency,
  busy,
  lang,
  t,
  onRecord,
}: {
  direction: 'spend' | 'receive';
  reference: ReferenceData;
  /** Open training courses this spend can be put on. Empty for most. */
  deliveries: TrainingDelivery[];
  currency: string;
  busy: boolean;
  lang: Lang;
  t: Translate;
  onRecord: (entry: MoneyEntry) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [bankAccountCode, setBankAccountCode] = useState('');
  const [contactId, setContactId] = useState('');
  const [date, setDate] = useState(today);
  const [memo, setMemo] = useState('');
  const [amountsAre, setAmountsAre] = useState<LineAmountType>('Exclusive');
  const [lines, setLines] = useState<MoneyLineEntry[]>([{ ...BLANK }]);
  const [deliveryId, setDeliveryId] = useState('');

  const spending = direction === 'spend';

  /* A closed course takes nothing more, so it is not offered. */
  const openCourses = deliveries.filter((delivery) => delivery.closedAt === null);

  const bankOptions: SelectOption[] = bankAccounts(reference.accounts).map((account) => ({
    value: account.code,
    label: `${account.code} · ${pickForLanguage(account.name, account.description, lang)}`,
  }));

  const contactOptions: SelectOption[] = reference.contacts.map((contact) => ({
    value: contact.id,
    label: contact.name,
  }));

  const categoryOptions: SelectOption[] = groupAccounts(categoryAccounts(reference.accounts))
    .map((group) => ({ label: t(accountTypeLabel(group.type)), accounts: group.accounts }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .flatMap((group) =>
      group.accounts.map((account) => ({
        value: account.code,
        label: `${account.code} · ${pickForLanguage(account.name, account.description, lang)}`,
        group: group.label,
      })),
    );

  const taxOptions: SelectOption[] = reference.taxRates.map((rate) => ({
    value: rate.type,
    label: t(rate.name),
  }));

  const setLine = (index: number, key: keyof MoneyLineEntry, value: string) => {
    setLines((current) => current.map((line, i) => (i === index ? { ...line, [key]: value } : line)));
  };

  /*
    The totals the invoice form computes, from the same function.

    A quantity of one with the typed figure as the unit price is exactly what
    is sent to Xero, so adding the lines up any other way here would show a
    number the ledger then disagrees with.
  */
  const totals = computeTotals(
    lines.map((line) => ({
      itemCode: '',
      description: line.description,
      qty: '1',
      price: line.amount,
      disc: '',
      accountCode: line.accountCode,
      taxType: line.taxType,
    })),
    amountsAre,
    reference.taxRates,
  );

  const entered = lines.filter((line) => line.amount.trim() !== '');
  const missingCategory = entered.some((line) => line.accountCode === '');
  const ready =
    bankAccountCode !== '' &&
    contactId !== '' &&
    entered.length > 0 &&
    !missingCategory &&
    totals.total > 0 &&
    !busy;

  const field = {
    height: '36px',
    width: '100%',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  const label = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' };

  const cell = {
    padding: '7px 8px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
    verticalAlign: 'top' as const,
  };

  const heading = {
    padding: '9px 8px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    textAlign: 'start' as const,
    borderBottom: `1px solid ${theme.border}`,
  };

  return (
    <Card>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.dividerSoft}`,
          fontWeight: 600,
          fontSize: '13px',
          background: theme.surfaceHeader,
        }}
      >
        {spending ? t('Money out') : t('Money in')}
      </div>

      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={label}>{spending ? t('Paid from') : t('Received into')}</span>
            <SearchableSelect
              value={bankAccountCode}
              options={bankOptions}
              placeholder={t('Choose…')}
              emptyLabel={t('No bank account in Xero for this company')}
              onChange={setBankAccountCode}
              t={t}
            />
          </label>

          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={label}>{spending ? t('Paid to') : t('Received from')}</span>
            <SearchableSelect
              value={contactId}
              options={contactOptions}
              placeholder={t('Choose…')}
              emptyLabel={t('No contacts in Xero for this organisation yet')}
              onChange={setContactId}
              t={t}
            />
          </label>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
          <label style={{ flex: '0 1 170px', minWidth: 0 }}>
            <span style={label}>{t('Date')}</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={field} />
          </label>

          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={label}>
              {t('Reference')}{' '}
              <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
            </span>
            <input
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder={t('Bank transfer number, cheque number')}
              style={field}
            />
          </label>

          <label style={{ flex: '0 1 190px', minWidth: 0 }}>
            <span style={label}>{t('Amounts are')}</span>
            <select
              value={amountsAre}
              onChange={(e) => setAmountsAre(e.target.value as LineAmountType)}
              style={field}
            >
              <option value="Exclusive">{t('Tax exclusive')}</option>
              <option value="Inclusive">{t('Tax inclusive')}</option>
              <option value="NoTax">{t('No tax')}</option>
            </select>
          </label>

          {/*
            Put this on a training course as it is filed, so nobody has to
            remember to go back and attach it. Only for money going out —
            money coming in is revenue, and a course's revenue comes from
            its invoices. Drawn only where there are open courses.
          */}
          {spending && openCourses.length > 0 && (
            <label style={{ flex: '1 1 260px', minWidth: 0 }}>
              <span style={label}>
                {t('For a training course')}{' '}
                <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
              </span>
              <select
                value={deliveryId}
                onChange={(e) => setDeliveryId(e.target.value)}
                style={field}
              >
                <option value="">{t('Not for a course')}</option>
                {openCourses.map((delivery) => (
                  <option key={delivery.deliveryId} value={String(delivery.deliveryId)}>
                    {delivery.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
          <thead>
            <tr>
              <th style={{ ...heading, width: '36%' }}>{t('Description')}</th>
              <th style={{ ...heading, width: '28%' }}>{t('Category')}</th>
              <th style={{ ...heading, width: '18%' }}>{t('Tax rate')}</th>
              <th style={{ ...heading, width: '14%' }}>{t('Amount')}</th>
              <th style={{ ...heading, width: '38px' }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index}>
                <td style={cell}>
                  <input
                    value={line.description}
                    onChange={(e) => setLine(index, 'description', e.target.value)}
                    placeholder={spending ? t('What was bought') : t('What it was for')}
                    style={field}
                  />
                </td>
                <td style={cell}>
                  <SearchableSelect
                    value={line.accountCode}
                    options={categoryOptions}
                    placeholder={t('Choose…')}
                    emptyLabel={t('No accounts found in Xero')}
                    invalid={line.amount.trim() !== '' && line.accountCode === ''}
                    onChange={(value) => setLine(index, 'accountCode', value)}
                    t={t}
                  />
                </td>
                <td style={cell}>
                  <SearchableSelect
                    value={line.taxType}
                    options={taxOptions}
                    placeholder={t('Account default')}
                    emptyLabel={t('No tax rates found in Xero')}
                    onChange={(value) => setLine(index, 'taxType', value)}
                    t={t}
                  />
                </td>
                <td style={cell}>
                  <input
                    value={line.amount}
                    onChange={(e) => setLine(index, 'amount', e.target.value)}
                    inputMode="decimal"
                    dir="ltr"
                    style={{ ...field, fontFamily: theme.fontMono, textAlign: 'end' }}
                  />
                </td>
                <td style={{ ...cell, textAlign: 'center' }}>
                  {/* The last row never goes: a grid with no rows has no way
                      back to having one. */}
                  <button
                    type="button"
                    disabled={lines.length === 1}
                    onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                    title={t('Remove')}
                    style={{
                      height: '30px',
                      width: '30px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: theme.radiusControl,
                      background: 'transparent',
                      color: lines.length === 1 ? theme.textFaint : theme.textMuted,
                      cursor: lines.length === 1 ? 'default' : 'pointer',
                      fontSize: '15px',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button
          type="button"
          onClick={() => setLines((current) => [...current, { ...BLANK }])}
          style={{
            background: 'transparent',
            border: `1px dashed ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            padding: '8px 14px',
            fontSize: '12.5px',
            fontWeight: 600,
            color: theme.text,
            cursor: 'pointer',
            marginBottom: '16px',
          }}
        >
          + {t('Add another line')}
        </button>

        <div
          style={{
            display: 'grid',
            gap: '4px',
            justifyContent: 'end',
            textAlign: 'end',
            fontSize: '12.5px',
            marginBottom: '16px',
          }}
        >
          <div style={{ color: theme.textMuted }}>
            {t('Subtotal')}{' '}
            <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
              {formatMoney(totals.sub)} {currency}
            </span>
          </div>
          <div style={{ color: theme.textMuted }}>
            {t('Total tax')}{' '}
            <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
              {formatMoney(totals.tax)} {currency}
            </span>
          </div>
          <div style={{ fontWeight: 700, fontSize: '14px' }}>
            {spending ? t('Total out') : t('Total in')}{' '}
            <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
              {formatMoney(totals.total)} {currency}
            </span>
          </div>
        </div>

        {missingCategory && (
          <div
            style={{ fontSize: '12.5px', color: theme.badDeep, fontWeight: 600, marginBottom: '12px' }}
          >
            {t('Every line with an amount needs a category.')}
          </div>
        )}

        <button
          type="button"
          disabled={!ready}
          onClick={() =>
            onRecord({
              kind: direction,
              contactId,
              bankAccountCode,
              date,
              reference: memo,
              amountsAre,
              lines,
              deliveryId: spending && deliveryId !== '' ? Number(deliveryId) : null,
            })
          }
          style={{
            height: '42px',
            minWidth: '220px',
            background: ready ? theme.nav : theme.borderStrong,
            color: '#fff',
            border: 0,
            borderRadius: theme.radiusControl,
            fontSize: '14px',
            fontWeight: 600,
            cursor: ready ? 'pointer' : 'default',
          }}
        >
          {busy ? t('Recording…') : spending ? t('Record money out') : t('Record money in')}
        </button>

        <div style={{ marginTop: '9px', fontSize: '11.5px', color: theme.textMuted, lineHeight: 1.6 }}>
          {t('This is recorded in Xero straight away. Money out and money in have no draft to approve.')}
        </div>
      </div>
    </Card>
  );
}
